import {
  Anchor,
  Box,
  Button,
  Container,
  Grid,
  Group,
  SimpleGrid,
  Stack,
  Text,
  Title,
} from "@mantine/core";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import {
  ALL_SUPPORTED_BARCODE_TYPES,
  COMMON_BARCODE_IDS,
  isCommonBarcodeId,
  type CommonBarcodeId,
} from "../shared/supportedBarcodes";
import { NavBarContainer } from "./NavBarContainer";

const cardStyle: React.CSSProperties = {
  background: "white",
  border: "1px solid var(--mantine-color-gray-2)",
  borderRadius: "var(--mantine-radius-md)",
  boxShadow: "0 8px 32px rgba(44, 45, 48, 0.06)",
  height: "100%",
};

function commonNameKey(id: CommonBarcodeId): `barcodes.common.${CommonBarcodeId}.name` {
  return `barcodes.common.${id}.name`;
}

function commonDescriptionKey(
  id: CommonBarcodeId,
): `barcodes.common.${CommonBarcodeId}.description` {
  return `barcodes.common.${id}.description`;
}

export function BarcodesPage() {
  const { t } = useTranslation();

  return (
    <NavBarContainer fullBleed showFooter>
      <Box
        style={{
          background:
            "linear-gradient(160deg, #fff8f2 0%, #ffffff 40%, #fff4eb 100%)",
        }}
        py={{ base: "xl", md: 48 }}
      >
        <Container size="lg">
          <Stack gap="xl">
            <Stack gap="sm" maw={720}>
              <Title order={1} c="charcoal.8">
                {t("barcodes.title")}
              </Title>
              <Text c="dimmed" size="lg">
                {t("barcodes.intro")}
              </Text>
              <Group gap="sm">
                <Button component={Link} to="/install" color="brand">
                  {t("barcodes.ctaInstall")}
                </Button>
                <Button
                  component={Link}
                  to="/tutorials#tutorial7"
                  variant="default"
                >
                  {t("barcodes.ctaTutorials")}
                </Button>
              </Group>
            </Stack>

            <Stack gap="md">
              <div>
                <Title order={2} c="charcoal.8">
                  {t("barcodes.commonTitle")}
                </Title>
                <Text c="dimmed" mt={6}>
                  {t("barcodes.commonIntro")}
                </Text>
              </div>
              <Grid>
                {COMMON_BARCODE_IDS.map((id) => (
                  <Grid.Col key={id} span={{ base: 12, sm: 6, md: 4 }}>
                    <Stack gap={6} p="lg" style={cardStyle}>
                      <Text fw={700} c="charcoal.8">
                        {t(commonNameKey(id))}
                      </Text>
                      <Text size="sm" c="dimmed">
                        {t(commonDescriptionKey(id))}
                      </Text>
                    </Stack>
                  </Grid.Col>
                ))}
              </Grid>
            </Stack>

            <Stack gap="md" id="all-types">
              <div>
                <Title order={2} c="charcoal.8">
                  {t("barcodes.fullTitle")}
                </Title>
                <Text c="dimmed" mt={6}>
                  {t("barcodes.fullIntro")}
                </Text>
              </div>
              <Box p="lg" style={cardStyle}>
                <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="xs">
                  {ALL_SUPPORTED_BARCODE_TYPES.map((name) => (
                    <Text
                      key={name}
                      size="sm"
                      fw={isCommonBarcodeId(name) ? 700 : 400}
                      c="charcoal.7"
                    >
                      {name}
                    </Text>
                  ))}
                </SimpleGrid>
              </Box>
              <Text size="sm" c="dimmed">
                {t("barcodes.fullCount", {
                  count: ALL_SUPPORTED_BARCODE_TYPES.length,
                })}{" "}
                <Anchor component={Link} to="/install">
                  {t("barcodes.ctaInstall")}
                </Anchor>
              </Text>
            </Stack>
          </Stack>
        </Container>
      </Box>
    </NavBarContainer>
  );
}
